import apiService from './ApiService';
import timeService from './TimeService';

const DEFAULT_SCHEDULER_HOUR = 22;
const DEFAULT_SCHEDULER_MINUTE = 5;

const IDB_NAME = 'concordare-config';
const IDB_VERSION = 1;
const IDB_STORE = 'config';
const IDB_KEY = 'public-config';

function parseCronSchedulerTime(cron) {
  // Formato: "0 MM HH * * *"
  const parts = (cron || '').split(' ');
  const hour = parseInt(parts[2], 10);
  const minute = parseInt(parts[1], 10);
  return {
    hour: isNaN(hour) ? DEFAULT_SCHEDULER_HOUR : hour,
    minute: isNaN(minute) ? DEFAULT_SCHEDULER_MINUTE : minute,
  };
}

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, IDB_VERSION);
    request.onupgradeneeded = (e) => {
      e.target.result.createObjectStore(IDB_STORE);
    };
    request.onsuccess = (e) => resolve(e.target.result);
    request.onerror = (e) => reject(e.target.error);
  });
}

function idbGet(db, key) {
  return new Promise((resolve, reject) => {
    const req = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key);
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

function idbPut(db, key, value) {
  return new Promise((resolve, reject) => {
    const req = db.transaction(IDB_STORE, 'readwrite').objectStore(IDB_STORE).put(value, key);
    req.onsuccess = () => resolve();
    req.onerror = (e) => reject(e.target.error);
  });
}

class ConfigService {
  constructor() {
    this._config = null;
    this._loadPromise = null;
  }

  async load() {
    if (this._config) return this._config;
    if (this._loadPromise) return this._loadPromise;
    this._loadPromise = this._loadWithCache();
    return this._loadPromise;
  }

  async _loadWithCache() {
    try {
      const db = await openDB();
      const cached = await idbGet(db, IDB_KEY);

      if (cached) {
        // Verificar si la versión remota coincide con la cacheada
        try {
          const { version } = await apiService.get('/api/v1/admin/public-config/version', false);
          if (version && version === cached.version) {
            // Caché válida: usar sin re-descargar la config completa
            this._config = cached.data;
            return this._config;
          }
        } catch {
          // Sin red: usar caché como fallback
          this._config = cached.data;
          return this._config;
        }
      }

      // Sin caché o versión desactualizada: descargar config completa
      const data = await apiService.get('/api/v1/admin/public-config', false);
      this._config = data || {};
      const version = this._config['CONFIG_VERSION'] || '0';
      await idbPut(db, IDB_KEY, { data: this._config, version });
      return this._config;
    } catch {
      this._config = this._config || {};
      return this._config;
    }
  }

  /**
   * Devuelve { hour, minute } de la hora programada del scheduler (zona Europe/Madrid).
   * Requiere haber llamado a load() antes, o usar getSchedulerTimeAsync().
   */
  getSchedulerTime() {
    if (!this._config) return { hour: DEFAULT_SCHEDULER_HOUR, minute: DEFAULT_SCHEDULER_MINUTE };
    return parseCronSchedulerTime(this._config['SCHEDULER_CRON']);
  }

  async getSchedulerTimeAsync() {
    await this.load();
    return this.getSchedulerTime();
  }

  /** Milisegundos hasta la próxima ejecución del scheduler (en hora Europe/Madrid). */
  getMsUntilSchedulerRun() {
    const { hour, minute } = this.getSchedulerTime();
    return getMsUntilTime(hour, minute);
  }

  /** true si la hora actual (en Europe/Madrid) ya ha superado la hora del scheduler hoy. */
  isPastSchedulerTime() {
    const { hour, minute } = this.getSchedulerTime();
    const madridTime = new Date(timeService.getServerNow()).toLocaleString('en-US', {
      timeZone: 'Europe/Madrid',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    const [currentHour, currentMinute] = madridTime.split(':').map(Number);
    return currentHour > hour || (currentHour === hour && currentMinute >= minute);
  }
}

/**
 * Calcula los ms hasta la próxima vez que el reloj de Madrid marque HH:MM.
 * Si ya ha pasado, apunta a mañana.
 */
export function getMsUntilTime(hour, minute) {
  const now = new Date(timeService.getServerNow());
  const madridTime = now.toLocaleString('en-US', {
    timeZone: 'Europe/Madrid',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const [currentHour, currentMinute, currentSecond] = madridTime.split(':').map(Number);

  const currentTotalSeconds = currentHour * 3600 + currentMinute * 60 + currentSecond;
  const targetTotalSeconds = hour * 3600 + minute * 60;

  let diffSeconds = targetTotalSeconds - currentTotalSeconds;
  if (diffSeconds < 0) {
    diffSeconds += 24 * 3600; // mañana
  }
  return diffSeconds * 1000;
}

export default new ConfigService();
